import React, { useState, useEffect } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/User/Header/Header';
import { CartProvider, useCart } from './cart/CartContext';
import Cart from './components/User/Cart/Cart';
import Hero from './components/User/Hero/Hero';
import About from './components/User/About/About';
import Gallery from './components/User/Gallery/Gallery';
import ProductCard from './components/User/ProductCard/ProductCard';
import ProductModal from './components/User/ProductModal/ProductModal';
import Testimonials from './components/User/Testimonials/Testimonials';
import Contacts from './components/User/Contacts/Contacts';
import Footer from './components/User/Footer/Footer';
import SuccessPage from './components/SuccessPage/SuccessPage';
import SeoHead from './seo/SeoHead';
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from './seo/siteConfig';
import publicApi from './services/publicApi';
import { initTelegramWebApp } from './services/telegramWebApp';

const LegacyAdminRedirect = () => <Navigate to="/" replace />;

function MainSite() {
    const cart = useCart();
    const [cartOpen, setCartOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadProducts();
        const refresh = () => {
            if (document.visibilityState === 'visible') loadProducts(false);
        };
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, []);

    const loadProducts = async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
                setError(null);
            }
            const data = await publicApi.catalog.getAll();
            const availableProducts = data.filter(product => product.is_active !== false && Number(product.total_quantity) > 0);
            setProducts(availableProducts);
            setSelectedProduct(current => current
                ? availableProducts.find(product => product.id === current.id) || null
                : null);
            setError(null);
        } catch (err) {
            if (showLoading) setError('Ошибка загрузки товаров');
            console.error(err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
    };

    const handleCloseModal = () => {
        setSelectedProduct(null);
    };

    const catalogProducts = [...products].sort((left, right) => {
        const leftPriority = left.category === 'tshirt' ? 0 : 1;
        const rightPriority = right.category === 'tshirt' ? 0 : 1;
        return leftPriority - rightPriority || left.id - right.id;
    });

    return (
        <div className="App">
            <SeoHead title={DEFAULT_TITLE} description={DEFAULT_DESCRIPTION} path="/" />
            <Header cartCount={cart?.count || 0} onOpenCart={() => setCartOpen(true)} />
            {cartOpen && <Cart onClose={() => setCartOpen(false)} />}
            <main>
                <Hero
                    products={products}
                    loading={loading}
                    error={error}
                    onRetry={loadProducts}
                    onProductClick={handleProductClick}
                />

                <section id="products" className="products-section" aria-labelledby="products-title">
                    <div className="container">
                        <div className="products-header">
                            <div>
                                <p className="section-kicker">Коллекция</p>
                                <h2 id="products-title">Выберите свою.</h2>
                            </div>
                            <p className="products-intro">
                                Футболки и лонгсливы из актуального наличия. Нажмите на вещь,
                                чтобы посмотреть ракурсы, выбрать размер и увидеть реальный остаток.
                            </p>
                        </div>

                        {loading && <div className="loading">Загрузка товаров...</div>}
                        {error && <div className="error" role="alert">{error}</div>}

                        {!loading && !error && catalogProducts.length === 0 && (
                            <p className="loading">Сейчас нет товаров в наличии. Загляните позже.</p>
                        )}

                        {!loading && !error && catalogProducts.length > 0 && (
                            <div className="products-grid">
                                {catalogProducts.map(product => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onProductClick={handleProductClick}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <Gallery products={products} />
                <About />

                {selectedProduct && (
                    <ProductModal
                        product={selectedProduct}
                        onClose={handleCloseModal}
                        cartItems={cart?.items || []}
                        cartPending={Boolean(cart?.attempt)}
                        onAddToCart={(product, variant) => cart.dispatch({ type: 'add', product, variant })}
                        onOpenCart={() => { setSelectedProduct(null); setCartOpen(true); }}
                    />
                )}

                <Testimonials />
                <Contacts />
            </main>
            <Footer />
        </div>
    );
}

function App() {
    useEffect(() => {
        initTelegramWebApp();
    }, []);

    return (
        <CartProvider><Routes>
            <Route
                path="/admin/*"
                element={<LegacyAdminRedirect />}
            />
            <Route
                path="/success"
                element={
                    <>
                        <SeoHead title="Статус оплаты" path="/success" noIndex />
                        <SuccessPage />
                    </>
                }
            />
            <Route path="/*" element={<MainSite />} />
        </Routes></CartProvider>
    );
}

export default App;
